import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import useDocumentMeta from './useDocumentMeta';
import logo from './assets/images/gladiators-hero.png';
import './GladiatorGames.css';
import { db, collection, addDoc } from './firebase';
import { query, where, getCountFromServer } from 'firebase/firestore';

const SKATER_CAPACITY = 30;
/** Set to true to always show the sold-out banner (e.g. registration closed manually). */
const SOLD_OUT_OVERRIDE = true;

const GladiatorGames = () => {
  const [formData, setFormData] = useState({
    parentFirstName: '',
    parentLastName: '',
    email: '',
    phone: '',
    skaterName: '',
    position: '',
    birthYear: '',
    currentHockeySituation: '',
    yearsOfExperience: '',
    excitedAbout: [],
    futureEvents: '',
    programImportance: [],
    howDidYouHear: '',
    readyToBeGladiator: false,
    wantsAdditionalTickets: '',
    additionalTicketsCount: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [skaterCount, setSkaterCount] = useState(null);

  const fetchSkaterCount = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'gladiator-games-registrations'),
        where('position', 'in', ['Forward', 'Defense'])
      );
      const snap = await getCountFromServer(q);
      setSkaterCount(snap.data().count);
    } catch (err) {
      console.error('Error fetching skater count:', err);
      setSkaterCount(0);
    }
  }, []);

  useEffect(() => {
    fetchSkaterCount();
  }, [fetchSkaterCount]);

  const skaterSpotsLeft = skaterCount !== null ? Math.max(0, SKATER_CAPACITY - skaterCount) : null;

  useDocumentMeta({
    title: 'Gladiator Games - March 21st at Huntington Center | Glass City Gladiators',
    description: 'Join us for Gladiator Games on March 21st at 2:30 PM! Play at the Huntington Center and get 2 tickets to the Walleye game. Open to 12U & 14U players.',
    canonical: 'https://gcghockey.com/gladiator-games',
    ogImage: 'https://gcghockey.com/assets/og-gladiator-games.png',
    ogType: 'website'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle multi-select checkboxes
    if (type === 'checkbox' && (name === 'excitedAbout' || name === 'programImportance')) {
      setFormData(prev => ({
        ...prev,
        [name]: checked 
          ? [...prev[name], value]
          : prev[name].filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    if (formData.position === 'Goalie') {
      setSubmitMessage('Sorry, goalie spots are sold out. Skater spots may still be available.');
      setIsSubmitting(false);
      return;
    }
    if (skaterSpotsLeft !== null && skaterSpotsLeft <= 0) {
      setSubmitMessage('Sorry, all skater spots are sold out.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Add the form data to Firestore
      await addDoc(collection(db, 'gladiator-games-registrations'), {
        ...formData,
        submittedAt: new Date().toISOString(),
        eventDate: '2026-03-21',
        status: 'pending' // You can use this to track registration status
      });
      
      setSubmitMessage('SUCCESS! We\'ll be in touch soon about your approval to join the games 🏒');
      await fetchSkaterCount();
      
      // Reset form
      setFormData({
        parentFirstName: '',
        parentLastName: '',
        email: '',
        phone: '',
        skaterName: '',
        position: '',
        birthYear: '',
        currentHockeySituation: '',
        yearsOfExperience: '',
        excitedAbout: [],
        futureEvents: '',
        programImportance: [],
        howDidYouHear: '',
        readyToBeGladiator: false,
        wantsAdditionalTickets: '',
        additionalTicketsCount: ''
      });
    } catch (error) {
      setSubmitMessage('Oops! Something went wrong. Please try again or email us directly.');
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="gladiator-games">
      {/* Animated Background */}
      <div className="games-bg-overlay"></div>
      <div className="games-grid-pattern"></div>

      {/* Hero Section */}
      <section className="games-hero">
        <motion.div 
          className="games-hero-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.img 
            src={logo} 
            alt="Glass City Gladiators"
            className="games-logo"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          />
          
          <motion.h1 
            className="games-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            GLADIATOR GAMES
          </motion.h1>

          {/* Sold Out Banner */}
          {(SOLD_OUT_OVERRIDE || (skaterSpotsLeft !== null && skaterSpotsLeft <= 0)) && (
            <motion.div
              className="games-sold-out-banner"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="games-sold-out-title">SOLD OUT</div>
              <div className="games-sold-out-thanks">
                Thank you to everyone who registered! We can&apos;t wait to see you at the Huntington Center.
              </div>
            </motion.div>
          )}
          
          <motion.div 
            className="games-date-badge"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="games-date-main">March 21st, 2026</div>
            <div className="games-date-time">2:00 PM Puck Drop</div>
            <div className="games-date-availability">
              {skaterSpotsLeft !== null
                ? skaterSpotsLeft > 0
                  ? `${skaterSpotsLeft} skater spot${skaterSpotsLeft === 1 ? '' : 's'} left • Goalies sold out`
                  : 'Sold out'
                : 'Goalies sold out'}
            </div>
          </motion.div>

          <motion.div 
            className="games-venue"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <div className="games-venue-icon">📍</div>
            <div className="games-venue-name">Huntington Center</div>
            <div className="games-venue-city">Downtown Toledo</div>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          className="games-scroll-indicator"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
        >
          <div className="games-scroll-text">Scroll to Learn More</div>
          <div className="games-scroll-arrow">↓</div>
        </motion.div>
      </section>

      {/* Event Details Section */}
      <section className="games-details">
        <div className="games-container">
          <motion.h2 
            className="games-section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            The Ultimate Hockey Experience
          </motion.h2>

          <div className="games-features-grid">
            <motion.div 
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="games-feature-icon">🏒</div>
              <h3>Full Game at Huntington Center</h3>
              <p>Experience playing on professional ice where the Toledo Walleye compete. An hour and a half of full regulation game, capped at 3 lines and 2 goalies per bench.</p>
            </motion.div>

            <motion.div 
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="games-feature-icon">🎟️</div>
              <h3>2 Walleye Game Tickets</h3>
              <p>Stick around! Watch the Toledo Walleye face off against the Tahoe Knight Monsters at 7:15 pm. Your $80 registration includes 2 game tickets. This is also a poster night so show up early! Additional tickets available for purchase as well.</p>
            </motion.div>

            <motion.div 
              className="games-feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="games-feature-icon">⭐</div>
              <h3>Skate Like A Gladiator</h3>
              <p>Elite-level competition for 12U and 14U players. Think you've got what it takes? Prove it.</p>
            </motion.div>
          </div>

          {/* Price Banner */}
          <motion.div 
            className="games-price-banner"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="games-price-label">All-Inclusive Price</div>
            <div className="games-price-amount">$80</div>
            <div className="games-price-includes">
              ✓ Game Participation<br/>
              ✓ 2 Walleye Tickets<br/>
              ✓ Unforgettable Experience
            </div>
          </motion.div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section className="games-registration">
        <div className="games-container">
          <motion.div
            className="games-form-wrapper"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="games-form-title">Register Your Gladiator</h2>
            <p className="games-form-subtitle">Secure your spot for an unforgettable experience at the Huntington Center</p>

            <div className="games-availability-banner">
              <span className="games-availability-item games-availability-goalie">Goalies: Sold out</span>
              {skaterSpotsLeft !== null && (
                <span className="games-availability-item games-availability-skaters">
                  {skaterSpotsLeft === 0 ? 'Skater spots: Sold out' : `${skaterSpotsLeft} skater spot${skaterSpotsLeft === 1 ? '' : 's'} left`}
                </span>
              )}
            </div>

            <form onSubmit={handleSubmit} className="games-form">
              {/* Parent/Guardian Information */}
              <div className="games-form-section">
                <h3 className="games-form-section-title">Parent/Guardian Information</h3>
                
                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="parentFirstName">First Name *</label>
                    <input
                      type="text"
                      id="parentFirstName"
                      name="parentFirstName"
                      value={formData.parentFirstName}
                      onChange={handleChange}
                      required
                      placeholder="John"
                    />
                  </div>

                  <div className="games-form-group">
                    <label htmlFor="parentLastName">Last Name *</label>
                    <input
                      type="text"
                      id="parentLastName"
                      name="parentLastName"
                      value={formData.parentLastName}
                      onChange={handleChange}
                      required
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john.smith@email.com"
                    />
                  </div>

                  <div className="games-form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(419) 555-0123"
                    />
                  </div>
                </div>
              </div>

              {/* Skater Information */}
              <div className="games-form-section">
                <h3 className="games-form-section-title">Skater Information</h3>
                
                <div className="games-form-group">
                  <label htmlFor="skaterName">Skater's Full Name *</label>
                  <input
                    type="text"
                    id="skaterName"
                    name="skaterName"
                    value={formData.skaterName}
                    onChange={handleChange}
                    required
                    placeholder="Alex Smith"
                  />
                </div>

                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="position">Position *</label>
                    <select
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Position</option>
                      <option value="Forward">Forward</option>
                      <option value="Defense">Defense</option>
                      <option value="Goalie" disabled>Goalie (sold out)</option>
                    </select>
                  </div>

                  <div className="games-form-group">
                    <label htmlFor="birthYear">Birth Year *</label>
                    <select
                      id="birthYear"
                      name="birthYear"
                      value={formData.birthYear}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Year</option>
                      <option value="2012">2012 </option>
                      <option value="2013">2013 </option>
                      <option value="2014">2014 </option>
                      <option value="2015">2015 </option>
                    </select>
                  </div>
                </div>

                <div className="games-form-row">
                  <div className="games-form-group">
                    <label htmlFor="currentHockeySituation">What best describes your current hockey situation? *</label>
                    <select
                      id="currentHockeySituation"
                      name="currentHockeySituation"
                      value={formData.currentHockeySituation}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Option</option>
                      <option value="Playing on a travel team">Playing on a travel team</option>
                      <option value="Playing on a house/rec team">Playing on a house/rec team</option>
                      <option value="Between teams / looking for a team">Between teams / looking for a team</option>
                      <option value="New to competitive hockey">New to competitive hockey</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="games-form-group">
                    <label htmlFor="yearsOfExperience">How many years of organized hockey experience does your skater have? *</label>
                    <select
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Experience</option>
                      <option value="1-2 years">1-2 years</option>
                      <option value="3-4 years">3-4 years</option>
                      <option value="5+ years">5+ years</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Event Interest Section */}
              <div className="games-form-section">
                <h3 className="games-form-section-title">Tell Us More</h3>
                
                <div className="games-form-group">
                  <label>What are you most excited about for this event? (Select all that apply) *</label>
                  <div className="games-checkbox-group">
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="excited-professional-ice"
                        name="excitedAbout"
                        value="Playing on professional ice"
                        checked={formData.excitedAbout.includes('Playing on professional ice')}
                        onChange={handleChange}
                      />
                      <label htmlFor="excited-professional-ice">Playing on professional ice</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="excited-competition"
                        name="excitedAbout"
                        value="Testing skills against top competition"
                        checked={formData.excitedAbout.includes('Testing skills against top competition')}
                        onChange={handleChange}
                      />
                      <label htmlFor="excited-competition">Testing skills against top competition</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="excited-travel-hockey"
                        name="excitedAbout"
                        value="Exploring travel hockey opportunities"
                        checked={formData.excitedAbout.includes('Exploring travel hockey opportunities')}
                        onChange={handleChange}
                      />
                      <label htmlFor="excited-travel-hockey">Exploring travel hockey opportunities</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="excited-meeting"
                        name="excitedAbout"
                        value="Meeting new players and coaches"
                        checked={formData.excitedAbout.includes('Meeting new players and coaches')}
                        onChange={handleChange}
                      />
                      <label htmlFor="excited-meeting">Meeting new players and coaches</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="excited-walleye"
                        name="excitedAbout"
                        value="The Walleye game experience"
                        checked={formData.excitedAbout.includes('The Walleye game experience')}
                        onChange={handleChange}
                      />
                      <label htmlFor="excited-walleye">The Walleye game experience</label>
                    </div>
                  </div>
                </div>

                <div className="games-form-group">
                  <label htmlFor="futureEvents">We're planning more events! Would you be interested in hearing about future competitive opportunities? *</label>
                  <select
                    id="futureEvents"
                    name="futureEvents"
                    value={formData.futureEvents}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Option</option>
                    <option value="Yes, keep me informed about Spring events">Yes, keep me informed about Spring events</option>
                    <option value="Yes, keep me informed about Fall events">Yes, keep me informed about Fall events</option>
                    <option value="Yes, both Spring and Fall">Yes, both Spring and Fall</option>
                    <option value="Just here for this event">Just here for this event</option>
                  </select>
                </div>

                <div className="games-form-group">
                  <label>What's most important to you in a hockey program? (Select your top 2) *</label>
                  <div className="games-checkbox-group">
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-competition"
                        name="programImportance"
                        value="High-level competition"
                        checked={formData.programImportance.includes('High-level competition')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-competition">High-level competition</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-coaching"
                        name="programImportance"
                        value="Strong coaching and development"
                        checked={formData.programImportance.includes('Strong coaching and development')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-coaching">Strong coaching and development</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-culture"
                        name="programImportance"
                        value="Team culture and camaraderie"
                        checked={formData.programImportance.includes('Team culture and camaraderie')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-culture">Team culture and camaraderie</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-schedule"
                        name="programImportance"
                        value="Flexible schedule"
                        checked={formData.programImportance.includes('Flexible schedule')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-schedule">Flexible schedule</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-travel"
                        name="programImportance"
                        value="Tournament travel opportunities"
                        checked={formData.programImportance.includes('Tournament travel opportunities')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-travel">Tournament travel opportunities</label>
                    </div>
                    <div className="games-form-checkbox">
                      <input
                        type="checkbox"
                        id="importance-convenience"
                        name="programImportance"
                        value="Local convenience"
                        checked={formData.programImportance.includes('Local convenience')}
                        onChange={handleChange}
                      />
                      <label htmlFor="importance-convenience">Local convenience</label>
                    </div>
                  </div>
                </div>

                <div className="games-form-group">
                  <label htmlFor="howDidYouHear">How did you hear about Gladiator Games? *</label>
                  <select
                    id="howDidYouHear"
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Option</option>
                    <option value="Current Gladiators family">Current Gladiators family</option>
                    <option value="Social media">Social media</option>
                    <option value="Friend/word of mouth">Friend/word of mouth</option>
                    <option value="Hockey rink/coach recommendation">Hockey rink/coach recommendation</option>
                    <option value="Web search">Web search</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Additional Tickets Section */}
              <div className="games-form-section">
                <h3 className="games-form-section-title">Additional Walleye Tickets</h3>
                
                <div className="games-form-group">
                  <label htmlFor="wantsAdditionalTickets">Your participation comes with 2 Walleye tickets. Would you like to purchase any additional tickets at $19 per ticket? *</label>
                  <select
                    id="wantsAdditionalTickets"
                    name="wantsAdditionalTickets"
                    value={formData.wantsAdditionalTickets}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Option</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>

                {formData.wantsAdditionalTickets === 'Yes' && (
                  <div className="games-form-group">
                    <label htmlFor="additionalTicketsCount">How many tickets? *</label>
                    <select
                      id="additionalTicketsCount"
                      name="additionalTicketsCount"
                      value={formData.additionalTicketsCount}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Number</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="6">6</option>
                      <option value="7">7</option>
                      <option value="8">8</option>
                      <option value="9">9</option>
                      <option value="10">10</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Ready to be a Gladiator Checkbox */}
              <div className="games-form-section">
                <div className="games-form-checkbox">
                  <input
                    type="checkbox"
                    id="readyToBeGladiator"
                    name="readyToBeGladiator"
                    checked={formData.readyToBeGladiator}
                    onChange={handleChange}
                    required
                  />
                  <label htmlFor="readyToBeGladiator">
                    <strong>Yes, I'm ready to compete!</strong>
                    <span className="games-checkbox-subtext"></span>
                  </label>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="games-submit-btn"
                disabled={isSubmitting || (skaterSpotsLeft !== null && skaterSpotsLeft <= 0)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isSubmitting ? 'Submitting...' : 'Register for Gladiator Games'}
              </motion.button>

              {/* Submit Message */}
              {submitMessage && (
                <motion.div 
                  className={`games-submit-message ${submitMessage.includes('SUCCESS') ? 'success' : 'error'}`}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitMessage}
                </motion.div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="games-faq">
        <div className="games-container">
          <motion.h2 
            className="games-section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Frequently Asked Questions
          </motion.h2>

          <div className="games-faq-grid">
            <motion.div 
              className="games-faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h4>Who can participate?</h4>
              <p>Players born in 2012-2015 who are interested in joining the Glass City Gladiators 12U or 14U teams for Spring and Fall seasons.</p>
            </motion.div>

            <motion.div 
              className="games-faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h4>What's included in the $80?</h4>
              <p>Full game participation at Huntington Center AND 2 tickets to watch the Toledo Walleye vs Tahoe Knight Monsters that evening.</p>
            </motion.div>

            <motion.div 
              className="games-faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h4>Can I come to watch?</h4>
              <p>Yes families can come support their players without admission for the Gladiator Games.</p>
            </motion.div>
           
            <motion.div 
              className="games-faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h4>What should players bring?</h4>
              <p>Full hockey gear including helmet, gloves, skates, stick, and pads.</p>
            </motion.div>

            <motion.div 
              className="games-faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h4>When should we arrive?</h4>
              <p>Please arrive by 12:30 PM for check-in and warmups. Puck drops at 2:00 PM sharp and ice time is over at 3:45!</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action Footer */}
      <section className="games-cta-footer">
        <motion.div
          className="games-cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>Questions?</h2>
          <p>Contact us at <a href="mailto:gcghockey@gmail.com">gcghockey@gmail.com</a></p>
          
          <div className="games-social-links">
            <a href="https://www.facebook.com/profile.php?id=61559199442580" target="_blank" rel="noopener noreferrer">
              Facebook
            </a>
            <a href="https://www.instagram.com/gcghockey" target="_blank" rel="noopener noreferrer">
              Instagram
            </a>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default GladiatorGames;